<template>
  <div class="onevr-container" @click="handleOutsideClick">
    <!-- Premium Animated Background -->
    <div class="animated-bg">
      <div class="floating-orb orb-1"></div>
      <div class="floating-orb orb-2"></div>
      <div class="floating-orb orb-3"></div>
      <div class="floating-orb orb-4"></div>
      <div class="gradient-mesh"></div>
      <div class="particles"></div>
    </div>

    <!-- Main Content Wrapper -->
    <div class="home-view-content">
      <!-- Hero Section -->
      <section class="hero-section">
        <div class="hero-content">
          <div class="hero-badge">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              class="badge-icon"
            >
              <path
                d="M8 0L10.5 5.5L16 8L10.5 10.5L8 16L5.5 10.5L0 8L5.5 5.5L8 0Z"
                fill="currentColor"
              />
            </svg>
            <span class="badge-text">{{ $t("homepage.hero.badge") }}</span>
          </div>
          <h1 class="hero-title">
            <span class="title-line">{{
              $t("homepage.hero.title.line1")
            }}</span>
            <span class="title-line gradient-text">{{
              $t("homepage.hero.title.line2")
            }}</span>
          </h1>
          <p class="hero-description">
            {{ $t("homepage.hero.description") }}
          </p>
        </div>
      </section>

      <!-- Modern Features Grid with Images -->
      <section class="features-section">
        <div class="feature-grid">
          <article
            v-for="(card, index) in cards"
            :key="card.label"
            class="feature-card"
            @click.stop="card.action"
            :style="{ animationDelay: `${index * 0.05}s` }"
            tabindex="0"
            @keypress.enter="card.action"
            role="button"
          >
            <!-- Card Image Header -->
            <div class="card-image-wrapper">
              <img
                :src="card.imageUrl"
                :alt="card.fallbackLabel"
                class="card-image"
              />
              <div class="card-image-overlay"></div>
              <div class="card-image-gradient"></div>
            </div>

            <!-- Card Content -->
            <div class="card-inner">
              <!-- Floating Icon Badge -->
              <div class="card-icon-badge">
                <div class="icon-glow"></div>
                <img
                  :src="card.iconUrl"
                  class="card-icon"
                  :alt="`${card.fallbackLabel} Icon`"
                />
              </div>

              <div class="card-content">
                <h3 class="card-title">
                  {{ $t(card.label) || card.fallbackLabel }}
                </h3>
                <p class="card-description">{{ card.description }}</p>
              </div>

              <div class="card-footer">
                <span class="card-cta">{{
                  $t("homepage.cards.exploreMore")
                }}</span>
                <div class="card-arrow">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M7 3l7 7-7 7"
                      stroke="currentColor"
                      stroke-width="2.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <!-- Hover Effects -->
            <div class="card-shine"></div>
          </article>
        </div>
      </section>

      <!-- Integration Section -->
      <section class="integration-section">
        <div class="integration-container">
          <div class="section-badge">
            <span class="badge-dot"></span>
            <span class="badge-label">{{
              $t("homepage.integration.badge")
            }}</span>
          </div>
          <h2 class="integration-title">
            <span class="title-accent">{{
              $t("homepage.integration.title.accent")
            }}</span>
            {{ $t("homepage.integration.title.text") }}
          </h2>
          <p class="integration-subtitle">
            {{ $t("homepage.integration.subtitle") }}
          </p>

          <div class="integration-grid">
            <article
              class="integration-card"
              v-for="(item, idx) in integrationItems"
              :key="idx"
              :style="{ animationDelay: `${idx * 0.08}s` }"
            >
              <div class="integration-glow"></div>
              <div class="integration-icon-wrapper">
                <div class="icon-background"></div>
                <img
                  :src="item.icon"
                  class="integration-icon"
                  :alt="`${item.title} Icon`"
                />
              </div>
              <h3 class="integration-card-title">{{ item.title }}</h3>
              <p class="integration-card-description">{{ item.description }}</p>
            </article>
          </div>
        </div>
      </section>
    </div>

    <!-- Footer -->
    <!-- Footer -->
    <footer class="elegant-footer">
      <div class="footer-content">
        <div class="footer-grid">
          <!-- Company Info -->
          <div class="footer-column company-info">
            <div class="footer-logo-section">
              <img
                class="footer-logo"
                src="/assets/oneVRDarkBackLogo.png"
                alt="OneVR Logo"
              />
            </div>
            <p class="footer-tagline">
              {{ $t("homepage.footer.tagline") }}
            </p>
            <p class="footer-company-name">pinestack GmbH</p>
          </div>

          <!-- Contact Information -->
          <div class="footer-column">
            <h3 class="footer-heading">{{ $t("homepage.footer.company") }}</h3>
            <div class="footer-details">
              <div class="footer-address">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  class="footer-icon"
                >
                  <path
                    d="M8 1a5 5 0 00-5 5c0 4 5 9 5 9s5-5 5-9a5 5 0 00-5-5z"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <circle
                    cx="8"
                    cy="6"
                    r="2"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                </svg>
                <div>
                  <p>Postfach 1104</p>
                  <p>85626 Grasbrunn</p>
                </div>
              </div>
              <div class="footer-address">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  class="footer-icon"
                >
                  <path
                    d="M8 1a5 5 0 00-5 5c0 4 5 9 5 9s5-5 5-9a5 5 0 00-5-5z"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <circle
                    cx="8"
                    cy="6"
                    r="2"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                </svg>
                <div>
                  <p>Bülow Strasse 9</p>
                  <p>81679 München</p>
                </div>
              </div>
              <div class="footer-address">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  class="footer-icon"
                >
                  <path
                    d="M8 1a5 5 0 00-5 5c0 4 5 9 5 9s5-5 5-9a5 5 0 00-5-5z"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <circle
                    cx="8"
                    cy="6"
                    r="2"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                </svg>
                <div>
                  <p>An der Spinnerei 6</p>
                  <p>D-96047 Bamberg</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Get in Touch -->
          <div class="footer-column">
            <h3 class="footer-heading">
              {{ $t("homepage.footer.getInTouch") }}
            </h3>
            <div class="footer-details">
              <a href="mailto:office@pinestack.io" class="footer-link">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  class="footer-icon"
                >
                  <path
                    d="M2 5l5.5 3.5a2 2 0 002 0L15 5M3 13h10a2 2 0 002-2V5a2 2 0 00-2-2H3a2 2 0 00-2 2v6a2 2 0 002 2z"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                office@pinestack.io
              </a>
              <a
                href="https://www.pinestack.io"
                target="_blank"
                rel="noopener noreferrer"
                class="footer-link"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  class="footer-icon"
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="6"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <path
                    d="M2 8h12M8 2a10 10 0 010 12M8 2a10 10 0 000 12"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                </svg>
                www.pinestack.io
              </a>
              <button  @click="goToContact" class="footer-contact-button">
                <span>{{ $t("homepage.footer.connectButton") }}</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M3 8h10m0 0l-3-3m3 3l-3 3"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Footer Bottom -->
        <div class="footer-bottom">
          <p class="footer-copyright">
            © {{ new Date().getFullYear() }} pinestack GmbH.
            {{ $t("homepage.footer.copyright") }}
          </p>
          <div class="footer-legal">
            <a href="#" class="footer-legal-link">{{
              $t("homepage.footer.privacyPolicy")
            }}</a>
            <span class="footer-separator">•</span>
            <a href="#" class="footer-legal-link">{{
              $t("homepage.footer.termsOfService")
            }}</a>
            <span class="footer-separator">•</span>
            <a href="#" class="footer-legal-link">{{
              $t("homepage.footer.imprint")
            }}</a>
          </div>
        </div>
      </div>
    </footer>

    <!-- Dialog Components -->
    <ConnectivityDiagram ref="connectivityRef" style="margin-top: 15px" />
    <BookingBrief ref="bookingBriefRef" style="margin-top: 15px" />
    <AnalyticsBrief ref="analyticsRef" style="margin-top: 15px" />
    <ClientSegments ref="clientSegmentsRef" />
    <HardwareControlBrief
      ref="hardwareControlBriefRef"
      style="margin-top: 15px"
    />
    <Operations ref="operationsBriefRef" style="margin-top: 15px" />
    <Security ref="securityRef" style="margin-top: 15px" />
    <Finance ref="financeRef" style="margin-top: 15px" />
    <GeminiChatbot ref="geminiRef" style="margin-top: 15px" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

// Component Imports
import ConnectivityDiagram from "@/components/ConnectivityDiagram.vue";
import BookingBrief from "@/components/BookingBrief.vue";
import AnalyticsBrief from "@/components/AnalyticsBrief.vue";
import ClientSegments from "@/components/ClientSegments.vue";
import HardwareControlBrief from "@/components/HardwareControlBrief.vue";
import Operations from "@/components/Operations.vue";
import Security from "@/components/Security.vue";
import Finance from "@/components/Finance.vue";
import GeminiChatbot from "@/components/GeminiChatbot.vue";

const { t } = useI18n();
const router = useRouter();

// Refs
const connectivityRef = ref(null);
const bookingBriefRef = ref(null);
const analyticsRef = ref(null);
const clientSegmentsRef = ref(null);
const hardwareControlBriefRef = ref(null);
const operationsBriefRef = ref(null);
const securityRef = ref(null);
const financeRef = ref(null);
const geminiRef = ref(null);

// Methods
const handleOutsideClick = () => {};

const goToContact = () => {
  router.push({ name: "ContactUs" });
};

// Integration items
const integrationItems = computed(() => [
  {
    title: t("homepage.integrationItems.iot.title"),
    description: t("homepage.integrationItems.iot.description"),
    icon: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/home-automation.svg"
  },
  {
    title: t("homepage.integrationItems.thirdParty.title"),
    description: t("homepage.integrationItems.thirdParty.description"),
    icon: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/cloud.svg"
  },
  {
    title: t("homepage.integrationItems.analytics.title"),
    description: t("homepage.integrationItems.analytics.description"),
    icon: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/chart-areaspline.svg"
  },
  {
    title: t("homepage.integrationItems.automation.title"),
    description: t("homepage.integrationItems.automation.description"),
    icon: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/cog-outline.svg"
  }
]);

// Cards with Beautiful Images - Optimized for better fit
const cards = computed(() => [
  {
    label: "app.visualization",
    fallbackLabel: "Visualization",
    description: t("homepage.cards.visualization.description"),
    iconUrl:
      "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/vector-rectangle.svg",
    imageUrl: "/assets/VisualizationImage.png",
    action: () => router.push({ name: "Visualization" })
  },
  // Info Center - News/Information icon
  {
    label: "app.infoCenter",
    fallbackLabel: "Info Center",
    description: t("homepage.cards.infoCenter.description"),
    iconUrl:
      "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/newspaper-variant-outline.svg",
    imageUrl:
      "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&h=500&fit=crop&q=80",
    action: () => router.push({ name: "InfoCenter" })
  },

  {
    label: "app.incidents",
    fallbackLabel: "Incident Management",
    description: t("homepage.cards.incidents.description"),
    iconUrl:
      "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/alert-octagon-outline.svg",
    imageUrl: "/assets/incidentsImage.png",
    action: () => router.push({ name: "Incidents" })
  },
  {
    label: "app.platformConnectivity",
    fallbackLabel: "Platform Connectivity",
    description: t("homepage.cards.platformConnectivity.description"),
    iconUrl: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/lan-connect.svg",
    imageUrl: "/assets/connectivityImage.png",
    action: () => connectivityRef.value?.open?.()
  },
  {
    label: "app.booking",
    fallbackLabel: "Room Booking",
    description: t("homepage.cards.booking.description"),
    iconUrl:
      "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/calendar-clock.svg",
    imageUrl:
      "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&h=500&fit=crop&q=80",
    action: () => bookingBriefRef.value?.open?.()
  },
  {
    label: "app.analytics",
    fallbackLabel: "Analytics & Insights",
    description: t("homepage.cards.analytics.description"),
    iconUrl:
      "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/chart-box-outline.svg",
    imageUrl:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop&q=80",
    action: () => analyticsRef.value?.open?.()
  },
  {
    label: "app.esg",
    fallbackLabel: "ESG Dashboard",
    description: t("homepage.cards.esg.description"),
    iconUrl: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/leaf.svg",
    imageUrl:
      "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800&h=500&fit=crop&q=80",
    action: () => router.push({ name: "ESG" })
  },
  {
    label: "app.clientSegments",
    fallbackLabel: "Client Management",
    description: t("homepage.cards.clientSegments.description"),
    iconUrl:
      "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/account-group.svg",
    imageUrl: "/assets/clientsegmentImage.png",
    action: () => router.push("/client-segment-analytics")
  },
  {
    label: "app.hardware",
    fallbackLabel: "Hardware Control",
    description: t("homepage.cards.hardware.description"),
    iconUrl:
      "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/router-wireless-settings.svg",
    imageUrl: "/assets/roomControlImage.jpeg",
    action: () => hardwareControlBriefRef.value?.open?.()
  },
  {
    label: "app.operations",
    fallbackLabel: "Operations Management",
    description: t("homepage.cards.operations.description"),
    iconUrl: "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/cog-outline.svg",
    imageUrl:
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=500&fit=crop&q=80",
    action: () => operationsBriefRef.value?.open?.()
  },
  {
    label: "app.security",
    fallbackLabel: "Security & Access",
    description: t("homepage.cards.security.description"),
    iconUrl:
      "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/shield-check.svg",
    imageUrl:
      "https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=800&h=500&fit=crop&q=80",
    action: () => securityRef.value?.open?.()
  },
  {
    label: "app.assistant",
    fallbackLabel: "AI Assistant",
    description: t("homepage.cards.assistant.description"),
    iconUrl:
      "https://cdn.jsdelivr.net/npm/@mdi/svg@7.4.47/svg/robot-outline.svg",
    imageUrl: "/assets/chatbotImag.png",
    action: () => geminiRef.value?.open?.()
  }
]);

// Intersection Observer
onMounted(() => {
  const observerOptions = {
    threshold: 0.1,
    rootMargin: "0px 0px -50px 0px"
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("animate-in");
      }
    });
  }, observerOptions);

  document
    .querySelectorAll(".feature-card, .integration-card")
    .forEach((el) => observer.observe(el));
});
</script>

<style scoped>
/* ==================== CSS Variables ==================== */
:root {
  --primary-blue: #3b82f6;
  --primary-dark: #2563eb;
  --primary-light: #60a5fa;
  --secondary: #8b5cf6;
  --bg-light: #f8fafc;
  --bg-white: #ffffff;
  --text-dark: #1e293b;
  --text-gray: #64748b;
  --text-light: #94a3b8;
  --border-light: rgba(59, 130, 246, 0.1);
  --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.04);
  --shadow-md: 0 8px 24px rgba(0, 0, 0, 0.08);
  --shadow-lg: 0 20px 48px rgba(59, 130, 246, 0.15);
  --radius-sm: 12px;
  --radius-md: 20px;
  --radius-lg: 28px;
  --transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* ==================== Container ==================== */
.onevr-container {
  position: relative;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%);
  overflow-x: hidden;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
}

/* ==================== Animated Background ==================== */
.animated-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.floating-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.3;
  animation: float 25s ease-in-out infinite;
}

.orb-1 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.2), transparent 70%);
  top: -15%;
  left: -10%;
  animation-delay: 0s;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(
    circle,
    rgba(139, 92, 246, 0.15),
    transparent 70%
  );
  top: 40%;
  right: -8%;
  animation-delay: 3s;
}

.orb-3 {
  width: 450px;
  height: 450px;
  background: radial-gradient(
    circle,
    rgba(14, 165, 233, 0.18),
    transparent 70%
  );
  bottom: 20%;
  left: 20%;
  animation-delay: 6s;
}

.orb-4 {
  width: 400px;
  height: 400px;
  background: radial-gradient(
    circle,
    rgba(99, 102, 241, 0.12),
    transparent 70%
  );
  bottom: 50%;
  right: 15%;
  animation-delay: 9s;
}

@keyframes float {
  0%,
  100% {
    transform: translate(0, 0) scale(1) rotate(0deg);
  }
  25% {
    transform: translate(40px, -40px) scale(1.1) rotate(90deg);
  }
  50% {
    transform: translate(-30px, 30px) scale(0.9) rotate(180deg);
  }
  75% {
    transform: translate(30px, 40px) scale(1.05) rotate(270deg);
  }
}

.gradient-mesh {
  position: absolute;
  inset: 0;
  background: radial-gradient(
      at 20% 30%,
      rgba(59, 130, 246, 0.1),
      transparent 50%
    ),
    radial-gradient(at 80% 70%, rgba(139, 92, 246, 0.08), transparent 50%),
    radial-gradient(at 50% 50%, rgba(14, 165, 233, 0.06), transparent 50%);
  animation: meshRotate 40s linear infinite;
}

@keyframes meshRotate {
  from {
    transform: rotate(0deg) scale(1);
  }
  to {
    transform: rotate(360deg) scale(1.1);
  }
}

.particles {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
      2px 2px at 20% 30%,
      rgba(59, 130, 246, 0.3),
      transparent
    ),
    radial-gradient(2px 2px at 60% 70%, rgba(139, 92, 246, 0.3), transparent),
    radial-gradient(1px 1px at 50% 50%, rgba(14, 165, 233, 0.3), transparent);
  background-size: 250px 250px, 300px 300px, 200px 200px;
  animation: particlesMove 60s linear infinite;
}

@keyframes particlesMove {
  from {
    background-position: 0 0, 40px 60px, 130px 270px;
  }
  to {
    background-position: 250px 250px, 290px 310px, 380px 520px;
  }
}

/* ==================== Content Wrapper ==================== */
.home-view-content {
  position: relative;
  z-index: 1;
}

/* ==================== Hero Section ==================== */
.hero-section {
  padding: 140px 3rem 120px;
  text-align: center;
  max-width: 1300px;
  margin: 0 auto;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 28px;
  background: rgba(255, 255, 255, 0.95);
  border: 1.5px solid rgba(59, 130, 246, 0.25);
  border-radius: 50px;
  margin-bottom: 36px;
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.1);
  backdrop-filter: blur(12px);
  transition: var(--transition);
}

.hero-badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(59, 130, 246, 0.15);
}

.badge-icon {
  color: var(--primary-blue);
  animation: sparkle 3s ease-in-out infinite;
}

@keyframes sparkle {
  0%,
  100% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(180deg) scale(1.15);
  }
}

.badge-text {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1.8px;
  text-transform: uppercase;
  color: var(--primary-blue);
}

.hero-title {
  font-size: clamp(48px, 7vw, 82px);
  font-weight: 900;
  line-height: 1.1;
  margin: 0 0 32px;
  letter-spacing: -2.5px;
  color: var(--text-dark);
}

.title-line {
  display: block;
}

.gradient-text {
  background: linear-gradient(
    135deg,
    #3b82f6 0%,
    #2563eb 30%,
    #8b5cf6 70%,
    #7c3aed 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  background-size: 200% auto;
  animation: gradientShift 6s ease infinite;
}

@keyframes gradientShift {
  0%,
  100% {
    background-position: 0% center;
  }
  50% {
    background-position: 100% center;
  }
}

.hero-description {
  font-size: clamp(17px, 2vw, 21px);
  line-height: 1.8;
  color: var(--text-gray);
  max-width: 950px;
  margin: 0 auto;
}

/* ==================== Features Grid ==================== */
.features-section {
  padding: 80px 3rem 120px;
  max-width: 1600px;
  margin: 0 auto;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 36px;
}

.feature-card {
  position: relative;
  background: var(--bg-white);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: var(--transition);
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  opacity: 0;
  transform: translateY(40px);
}

.feature-card.animate-in {
  animation: fadeInUp 0.6s ease-out forwards;
}

@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.feature-card:hover {
  transform: translateY(-16px) scale(1.02);
  border-color: var(--primary-blue);
  box-shadow: var(--shadow-lg);
}

/* Card Image */
.card-image-wrapper {
  position: relative;
  width: 100%;
  height: 220px;
  overflow: hidden;
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.feature-card:hover .card-image {
  transform: scale(1.2);
}

.card-image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.3) 100%);
  transition: opacity 0.4s;
  z-index: 1;
}

.feature-card:hover .card-image-overlay {
  opacity: 0.7;
}

.card-image-gradient {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 120px;
  background: linear-gradient(to top, rgba(255, 255, 255, 0.98), transparent);
  z-index: 2;
}

/* Card Inner Content */
.card-inner {
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: relative;
}

.card-icon-badge {
  position: absolute;
  top: -32px;
  left: 32px;
  width: 68px;
  height: 68px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
  border-radius: 18px;
  border: 2px solid var(--bg-white);
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.2);
  transition: var(--transition);
  z-index: 3;
}

.feature-card:hover .card-icon-badge {
  background: linear-gradient(135deg, var(--primary-blue), var(--primary-dark));
  transform: scale(1.15) rotate(-10deg);
  box-shadow: 0 12px 32px rgba(59, 130, 246, 0.4);
}

.icon-glow {
  position: absolute;
  inset: -10px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary));
  border-radius: 18px;
  opacity: 0;
  filter: blur(20px);
  transition: opacity 0.4s;
  z-index: -1;
}

.feature-card:hover .icon-glow {
  opacity: 0.5;
}

.card-icon {
  width: 38px;
  height: 38px;
  filter: invert(38%) sepia(89%) saturate(2020%) hue-rotate(200deg)
    brightness(99%) contrast(93%);
  transition: filter 0.3s;
  z-index: 1;
}

.feature-card:hover .card-icon {
  filter: brightness(0) invert(1);
}

.card-content {
  margin-top: 44px;
  flex: 1;
}

.card-title {
  font-size: 26px;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0 0 14px;
  letter-spacing: -0.6px;
  transition: color 0.3s;
  line-height: 1.3;
}

.feature-card:hover .card-title {
  color: var(--primary-blue);
}

.card-description {
  font-size: 15px;
  line-height: 1.7;
  color: var(--text-gray);
  margin: 0;
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 20px;
  border-top: 1px solid rgba(226, 232, 240, 0.8);
}

.card-cta {
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-blue);
  letter-spacing: 0.5px;
  transition: transform 0.3s;
}

.feature-card:hover .card-cta {
  transform: translateX(4px);
}

.card-arrow {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
  border-radius: 50%;
  border: 1.5px solid rgba(59, 130, 246, 0.2);
  color: var(--primary-blue);
  transition: var(--transition);
}

.feature-card:hover .card-arrow {
  background: linear-gradient(135deg, var(--primary-blue), var(--primary-dark));
  color: white;
  transform: translateX(8px) scale(1.1);
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.3);
}

.card-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.8) 50%,
    transparent 100%
  );
  transition: left 0.7s;
  z-index: 10;
  pointer-events: none;
}

.feature-card:hover .card-shine {
  left: 100%;
}

/* ==================== Integration Section ==================== */
.integration-section {
  padding: 120px 3rem 140px;
  margin: 100px auto;
  max-width: 1500px;
  text-align: center;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 10px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(248, 250, 252, 0.95) 100%
  );
  border: 2px solid rgba(59, 130, 246, 0.3);
  border-radius: 50px;
  margin-bottom: 28px;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.12);
  backdrop-filter: blur(12px);
}

.badge-dot {
  width: 10px;
  height: 10px;
  background: linear-gradient(135deg, var(--primary-blue), var(--primary-dark));
  border-radius: 50%;
  animation: pulse 2.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
  }
  50% {
    transform: scale(1.2);
    box-shadow: 0 0 0 8px rgba(59, 130, 246, 0);
  }
}

.badge-label {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #2563eb;
}

.integration-title {
  font-size: clamp(42px, 6vw, 64px);
  font-weight: 800;
  color: var(--text-dark);
  margin: 0 0 28px;
  letter-spacing: -2px;
  line-height: 1.2;
}

.title-accent[data-v-9ea40744] {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary));
  -webkit-background-clip: text;
  -webkit-text-fill-color: blue;
  background-clip: text;
}

.integration-subtitle {
  font-size: clamp(16px, 2vw, 20px);
  line-height: 1.8;
  color: var(--text-gray);
  max-width: 950px;
  margin: 0 auto 72px;
}

.integration-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 40px;
  margin-top: 72px;
}

.integration-card {
  position: relative;
  background: var(--bg-white);
  padding: 48px 40px;
  border-radius: var(--radius-lg);
  transition: var(--transition);
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-sm);
  text-align: center;
  overflow: hidden;
  opacity: 0;
  transform: translateY(40px);
}

.integration-card.animate-in {
  animation: fadeInUp 0.7s ease-out forwards;
}

.integration-card:hover {
  transform: translateY(-20px) scale(1.03);
  border-color: var(--primary-blue);
  box-shadow: var(--shadow-lg);
}

.integration-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at center,
    rgba(59, 130, 246, 0.1),
    transparent 70%
  );
  opacity: 0;
  transition: opacity 0.5s;
}

.integration-card:hover .integration-glow {
  opacity: 1;
}

.integration-icon-wrapper {
  width: 120px;
  height: 120px;
  margin: 0 auto 32px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.6s var(--transition);
}

.icon-background {
  position: absolute;
  inset: 0;
  border-radius: 24px;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.12),
    rgba(139, 92, 246, 0.1)
  );
  transition: all 0.5s;
}

.integration-card:hover .icon-background {
  background: linear-gradient(135deg, var(--primary-blue), var(--primary-dark));
  box-shadow: 0 20px 40px rgba(59, 130, 246, 0.3);
}

.integration-card:hover .integration-icon-wrapper {
  transform: scale(1.2) rotate(-10deg);
}

.integration-icon {
  width: 56px;
  height: 56px;
  filter: invert(38%) sepia(89%) saturate(2020%) hue-rotate(200deg)
    brightness(99%) contrast(93%);
  transition: filter 0.4s;
  z-index: 1;
  position: relative;
}

.integration-card:hover .integration-icon {
  filter: brightness(0) invert(1);
}

.integration-card-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0 0 16px;
  letter-spacing: -0.7px;
  transition: color 0.3s;
  line-height: 1.3;
}

.integration-card:hover .integration-card-title {
  color: var(--primary-blue);
}

.integration-card-description {
  font-size: 15px;
  line-height: 1.7;
  color: var(--text-gray);
  margin: 0;
}

/* ==================== Footer ==================== */
.elegant-footer {
  position: relative;
  background: linear-gradient(135deg, #0a1929 0%, #1a2f42 100%);
  color: #e2e8f0;
  padding: 100px 0 0;
  margin-top: 140px;
  z-index: 10;
}

.footer-content {
  max-width: 1500px;
  margin: 0 auto;
  padding: 0 3rem;
}

.footer-grid {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr;
  gap: 80px;
  padding-bottom: 70px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-column {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.footer-logo {
  height: 55px;
  width: auto;
  filter: drop-shadow(0 4px 12px rgba(59, 130, 246, 0.25));
  transition: transform 0.3s;
}

.footer-logo:hover {
  transform: scale(1.05);
}

.footer-tagline {
  font-size: 16px;
  line-height: 1.7;
  color: #94a3b8;
  max-width: 350px;
}

.footer-heading {
  font-size: 19px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 12px;
  letter-spacing: 0.5px;
}

.footer-details {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.footer-address {
  display: flex;
  gap: 14px;
  align-items: start;
  font-size: 14px;
  line-height: 1.6;
  color: #cbd5e1;
  transition: transform 0.3s;
}

.footer-address:hover {
  transform: translateX(4px);
}

.footer-icon {
  flex-shrink: 0;
  color: var(--primary-blue);
  margin-top: 3px;
}

.footer-link {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 15px;
  color: #cbd5e1;
  text-decoration: none;
  transition: all 0.3s;
  width: fit-content;
}

.footer-link:hover {
  color: var(--primary-blue);
  transform: translateX(6px);
}

.footer-contact-button {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 28px;
  background: linear-gradient(135deg, var(--primary-blue), var(--primary-dark));
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.35);
  margin-top: 12px;
  width: fit-content;
}

.footer-contact-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(59, 130, 246, 0.45);
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 40px 0;
  flex-wrap: wrap;
  gap: 24px;
}

.footer-copyright {
  font-size: 14px;
  color: #94a3b8;
  margin: 0;
}

.footer-legal {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.footer-legal-link {
  font-size: 14px;
  color: #cbd5e1;
  text-decoration: none;
  transition: color 0.3s;
  position: relative;
}

.footer-legal-link::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: var(--primary-blue);
  transition: width 0.3s;
}

.footer-legal-link:hover {
  color: var(--primary-blue);
}

.footer-legal-link:hover::after {
  width: 100%;
}

.footer-separator {
  color: #475569;
}

/* ==================== Responsive ==================== */
@media (max-width: 1200px) {
  .feature-grid {
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: 32px;
  }
  .footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: 50px;
  }
  .company-info {
    grid-column: 1 / -1;
  }
}

@media (max-width: 768px) {
  .hero-section {
    padding: 80px 1.5rem 60px;
  }
  .features-section {
    padding: 50px 1.5rem 80px;
  }
  .feature-grid {
    grid-template-columns: 1fr;
    gap: 28px;
  }
  .card-image-wrapper {
    height: 200px;
  }
  .card-inner {
    padding: 28px 24px;
  }
  .card-icon-badge {
    width: 58px;
    height: 58px;
    left: 24px;
  }
  .card-icon {
    width: 32px;
    height: 32px;
  }
  .integration-section {
    padding: 70px 1.5rem 80px;
    margin: 50px auto;
  }
  .integration-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }
  .footer-grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  .footer-bottom {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 480px) {
  .hero-section {
    padding: 60px 1rem 50px;
  }
  .features-section {
    padding: 40px 1rem 60px;
  }
  .feature-grid {
    gap: 24px;
  }
  .card-image-wrapper {
    height: 180px;
  }
  .card-inner {
    padding: 24px 20px;
  }
  .card-icon-badge {
    width: 52px;
    height: 52px;
    left: 20px;
  }
  .card-title {
    font-size: 20px;
  }
  .integration-section {
    padding: 60px 1rem 70px;
  }
}

/* ==================== Accessibility ==================== */
.feature-card:focus-visible,
.integration-card:focus-visible {
  outline: 3px solid var(--primary-blue);
  outline-offset: 4px;
}
</style>
